import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { BaseService } from '@app/services';
import { DirectivesModule, PipesModule, SharedModule } from '@app/shared';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { AuthService, IReset } from '..';
import { buildNewPasswordValidators, STRONG_PASSWORD_HINT } from '../password-policy';
import { AuthShellComponent } from '../shell';
import { FormGroupInterface, FormGroupValuesInterface } from './reset.interface';

/**
 * Sets a new password from the e-mailed reset link (`?id=&token=`), then shows the
 * "password changed" confirmation instead of leaving the page.
 */
@Component({
  imports: [SharedModule, DirectivesModule, PipesModule, AuthShellComponent],
  selector: 'app-reset',
  templateUrl: './reset.component.html',
})
export class ResetComponent extends BaseService implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);

  formReset: FormGroup;
  protected readonly passwordHint: string = STRONG_PASSWORD_HINT;
  private readonly id = signal<string>('');
  private readonly token = signal<string>('');
  protected readonly isPasswordChanged = signal<boolean>(false);
  protected readonly hasResetLink = computed<boolean>(() => this.id().length > 0 && this.token().length > 0);

  constructor() {
    super();
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((params: Params) => this.onParamsChange(params));
  }

  ngOnInit(): void {
    this.createForm();
  }

  onParamsChange(params: { id?: string; token?: string }): void {
    this.id.set(params.id ?? '');
    this.token.set(params.token ?? '');
  }

  createForm(): void {
    this.formReset = new FormGroup<FormGroupInterface>({
      password: new FormControl('', buildNewPasswordValidators()),
      confirmPassword: new FormControl('', [RxwebValidators.required(), RxwebValidators.compare({ fieldName: 'password' })]),
    });
  }

  reset(): void {
    const formValues: FormGroupValuesInterface = this.formReset.getRawValue();
    const params: IReset = {
      id: this.id(),
      token: this.token(),
      password: formValues.password,
    };
    this._spin.start();
    this.authService
      .reset(params)
      .then(() => {
        this.isPasswordChanged.set(true);
      })
      .catch((e: Error) => {
        this._toast.error({ message: e.message });
      })
      .finally(() => {
        this._spin.stop();
      });
  }
}
